import React, { useState, useEffect } from 'react';
import { useUIStore, useUnifiedElementsStore, useFincasStore, useCategoriesStore } from '../../stores';
import { logger } from '../../../../shared/utils/logger';
import './CalculationsPanel.css';

interface CalculationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  mapService?: any;
}

interface BuiltAreaData {
  fincaId: number | null; // null para "Sin asignar"
  superficieConstruida: number;
  porcentaje: number;
}

interface FiscalData {
  fincaId: number | null; // null para "Sin asignar"
  superficieFiscal: number;
  superficieZonasComunes: number;
  porcentaje: number;
}

interface ZCPonderacionData {
  fincaId: number | null; // null para "Sin asignar"
  superficieFiscal: number;
  superficieZCAsignada: number;
  porcentajeDistribucion: number;
  superficieTotal: number;
}

const CalculationsPanel: React.FC<CalculationsPanelProps> = ({ 
  isOpen, 
  onClose, 
  mapService: _mapService 
}) => {
  const [activeTab, setActiveTab] = useState<'builtArea' | 'fiscal' | 'zc'>('builtArea');
  const [builtAreaData, setBuiltAreaData] = useState<BuiltAreaData[]>([]);
  const [fiscalData, setFiscalData] = useState<FiscalData[]>([]);
  const [zcData, setZcData] = useState<ZCPonderacionData[]>([]);
  const { addNotification } = useUIStore();
  const { elements } = useUnifiedElementsStore();
  const { getCategoryById } = useCategoriesStore();
  const { getAllFincas } = useFincasStore();

  // Función helper para obtener el nombre de visualización de una finca
  const getFincaDisplayName = (fincaId: number | null): string => {
    if (fincaId === null) return 'Sin asignar';
    if (!fincaId) return '';
    const fincas = getAllFincas();
    const finca = fincas.find(f => f.numeroFinca === fincaId);
    if (finca && finca.nombre) {
      return finca.nombre;
    }
    return `Finca ${fincaId}`;
  };



  // Función para obtener datos de superficie construida
  const calculateBuiltAreaData = (): BuiltAreaData[] => {
    const builtAreaByFinca: { [key: string]: BuiltAreaData } = {};

    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Calculando superficie construida...');
    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Elementos disponibles:', elements.length);
    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Elementos con categoría ZC (excluidos):', elements.filter(el => el.category === 'ZC').length);

    elements.forEach((element) => {
      // Excluir zonas comunes (ZC) del cálculo de superficie construida
      if (element.category === 'ZC') {
        logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: Excluyendo ZC ${element.id} - Área: ${element.area.toFixed(2)} m²`);
        return;
      }

      // Determinar el fincaId (null para "Sin asignar")
      const fincaId = element.finca && element.finca !== '' ? parseInt(element.finca) : null;
      const fincaKey = fincaId !== null ? fincaId.toString() : 'sin-asignar';
      const superficieConstruida = element.area;
      
      if (!builtAreaByFinca[fincaKey]) {
        builtAreaByFinca[fincaKey] = {
          fincaId: fincaId,
          superficieConstruida: 0,
          porcentaje: 0
        };
      }
      
      builtAreaByFinca[fincaKey].superficieConstruida += superficieConstruida;
      const fincaDisplay = fincaId !== null ? `Finca ${fincaId}` : 'Sin asignar';
              logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: Elemento ${element.category} (${fincaDisplay}) - Superficie construida: ${superficieConstruida.toFixed(2)} m²`);
    });

    const builtAreaData = Object.values(builtAreaByFinca).sort((a, b) => {
      // "Sin asignar" va al final
      if (a.fincaId === null) return 1;
      if (b.fincaId === null) return -1;
      return a.fincaId - b.fincaId;
    });
    
    const totalBuiltArea = builtAreaData.reduce((sum, finca) => sum + finca.superficieConstruida, 0);

    builtAreaData.forEach(finca => {
      finca.porcentaje = totalBuiltArea > 0 ? (finca.superficieConstruida / totalBuiltArea) * 100 : 0;
    });

    return builtAreaData;
  };

  // Función para calcular datos fiscales
  const calculateFiscalData = (): FiscalData[] => {
    const fiscalByFinca: { [key: string]: FiscalData } = {};

    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Calculando datos fiscales...');
    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Elementos disponibles:', elements.length);
    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Elementos con categoría ZC (excluidos):', elements.filter(el => el.category === 'ZC').length);

    // Calcular superficie fiscal por finca (excluyendo zonas comunes)
    elements.forEach((element) => {
      // Excluir zonas comunes (ZC) del cálculo de superficie fiscal
      if (element.category === 'ZC') {
        logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: Excluyendo ZC ${element.id} del cálculo fiscal - Área: ${element.area.toFixed(2)} m²`);
        return;
      }

      // Determinar cómputo efectivo: del elemento o, si falta, de su categoría
      const category = getCategoryById(element.category);
      const computoEfectivo = (element.computo !== null && !isNaN(Number(element.computo)))
        ? Number(element.computo)
        : (category?.computo ?? 100);

      if (computoEfectivo !== null) {
        const fincaId = element.finca && element.finca !== '' ? parseInt(element.finca) : null;
        const fincaKey = fincaId !== null ? fincaId.toString() : 'sin-asignar';
        
        // Calcular superficie fiscal ponderada: área * (cómputo efectivo / 100)
        const superficieFiscal = element.area * (computoEfectivo / 100);
        
        if (!fiscalByFinca[fincaKey]) {
          fiscalByFinca[fincaKey] = {
            fincaId: fincaId,
            superficieFiscal: 0,
            superficieZonasComunes: 0,
            porcentaje: 0
          };
        }
        
        fiscalByFinca[fincaKey].superficieFiscal += superficieFiscal;
        const fincaDisplay = fincaId !== null ? `Finca ${fincaId}` : 'Sin asignar';
        logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: Elemento ${element.category} (${fincaDisplay}) - Área: ${element.area.toFixed(2)} m², Cómputo efectivo: ${computoEfectivo}%, Superficie fiscal: ${superficieFiscal.toFixed(2)} m²`);
      }
    });

    const fiscalData = Object.values(fiscalByFinca).sort((a, b) => {
      // "Sin asignar" va al final
      if (a.fincaId === null) return 1;
      if (b.fincaId === null) return -1;
      return a.fincaId - b.fincaId;
    });
    
    const totalFiscal = fiscalData.reduce((sum, finca) => sum + finca.superficieFiscal, 0);

    // Calcular el total de zonas comunes (para mostrar en línea separada)
    const totalZonasComunes = elements
      .filter((element) => element.category === 'ZC')
      .reduce((sum: number, element) => sum + element.area, 0);

    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Total zonas comunes (todas):', totalZonasComunes.toFixed(2), 'm²');

    fiscalData.forEach(finca => {
      finca.porcentaje = totalFiscal > 0 ? (finca.superficieFiscal / totalFiscal) * 100 : 0;
      // Las zonas comunes se muestran en línea separada, no por finca
      finca.superficieZonasComunes = 0;
      const fincaDisplay = finca.fincaId !== null ? `Finca ${finca.fincaId}` : 'Sin asignar';
              logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: ${fincaDisplay} - Superficie fiscal: ${finca.superficieFiscal.toFixed(2)} m², Porcentaje: ${finca.porcentaje.toFixed(2)}%`);
    });

    return fiscalData;
  };

  // Función para calcular ponderación de zonas comunes
  const calculateZCPonderacion = (): ZCPonderacionData[] => {
    const ponderacionByFinca: { [key: string]: ZCPonderacionData } = {};

    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Calculando ponderación de zonas comunes...');

    // Calcular superficie fiscal por finca (EXCLUYENDO zonas comunes)
    elements.forEach((element) => {
      if (element.computo !== null && element.category !== 'ZC') {
        const fincaId = element.finca && element.finca !== '' ? parseInt(element.finca) : null;
        const fincaKey = fincaId !== null ? fincaId.toString() : 'sin-asignar';
        const superficieFiscal = element.area * (element.computo / 100);
        
        logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: Procesando elemento ${element.category} para superficie fiscal - Finca: "${element.finca}", Área: ${element.area.toFixed(2)} m², Cómputo: ${element.computo}%, Superficie fiscal: ${superficieFiscal.toFixed(2)} m²`);
        
        if (!ponderacionByFinca[fincaKey]) {
          ponderacionByFinca[fincaKey] = {
            fincaId: fincaId,
            superficieFiscal: 0,
            superficieZCAsignada: 0,
            porcentajeDistribucion: 0,
            superficieTotal: 0
          };
        }
        
        ponderacionByFinca[fincaKey].superficieFiscal += superficieFiscal;
      }
    });

    // Calcular total de superficie fiscal para el reparto proporcional
    const totalSuperficieFiscal = Object.values(ponderacionByFinca).reduce((sum, finca) => sum + finca.superficieFiscal, 0);
    logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: Total superficie fiscal para reparto: ${totalSuperficieFiscal.toFixed(2)} m²`);

    // Calcular total de zonas comunes a repartir
    const totalZonasComunes = elements
      .filter(element => element.category === 'ZC')
      .reduce((sum, element) => sum + element.area, 0);
    logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: Total zonas comunes a repartir: ${totalZonasComunes.toFixed(2)} m²`);

    // Repartir zonas comunes proporcionalmente según superficie fiscal
    Object.values(ponderacionByFinca).forEach(finca => {
      if (totalSuperficieFiscal > 0) {
        const porcentajeDistribucion = (finca.superficieFiscal / totalSuperficieFiscal) * 100;
        finca.superficieZCAsignada = (totalZonasComunes * porcentajeDistribucion) / 100;
        finca.porcentajeDistribucion = porcentajeDistribucion;
        finca.superficieTotal = finca.superficieFiscal + finca.superficieZCAsignada;
        
        const fincaDisplay = finca.fincaId !== null ? `Finca ${finca.fincaId}` : 'Sin asignar';
        console.log(`🔍 CalculationsPanel: ${fincaDisplay} - Superficie fiscal: ${finca.superficieFiscal.toFixed(2)} m², ZC asignada: ${finca.superficieZCAsignada.toFixed(2)} m² (${porcentajeDistribucion.toFixed(2)}%), Total: ${finca.superficieTotal.toFixed(2)} m²`);
      }
    });

    const ponderacionData = Object.values(ponderacionByFinca).sort((a, b) => {
      // "Sin asignar" va al final
      if (a.fincaId === null) return 1;
      if (b.fincaId === null) return -1;
      return a.fincaId - b.fincaId;
    });
    
    const totalFiscal = ponderacionData.reduce((sum, finca) => sum + finca.superficieFiscal, 0);

    ponderacionData.forEach(finca => {
      finca.porcentajeDistribucion = totalFiscal > 0 ? (finca.superficieFiscal / totalFiscal) * 100 : 0;
      finca.superficieTotal = finca.superficieFiscal + finca.superficieZCAsignada;
      const fincaDisplay = finca.fincaId !== null ? `Finca ${finca.fincaId}` : 'Sin asignar';
              logger.log('CALCULATIONS_PANEL', `🔍 CalculationsPanel: ${fincaDisplay} - ZC asignada: ${finca.superficieZCAsignada.toFixed(2)} m², Total: ${finca.superficieTotal.toFixed(2)} m²`);
    });

    return ponderacionData;
  };

  // Función para exportar a CSV
  const exportToCSV = (data: any[], headers: string[], fileName: string) => {
    if (!data || data.length === 0) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No hay datos disponibles para exportar.'
      });
      return;
    }

    const formatNumber = (num: number) => {
      return num.toFixed(2).replace('.', ',');
    };

    const csvContent = [
      headers.join(';'),
      ...data.map(row => Object.values(row).map(value => 
        typeof value === 'number' ? formatNumber(value) : value
      ).join(';'))
    ].join('\n');

    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addNotification({
      type: 'success',
      title: 'Éxito',
      message: `Archivo CSV exportado: ${fileName}`
    });
  };

  // Actualizar datos cuando cambie el tab activo o los elementos
  useEffect(() => {
    logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: useEffect ejecutado - isOpen:', isOpen, 'activeTab:', activeTab, 'elements.length:', elements.length);
    
    if (isOpen) {
      // Usar setTimeout para evitar interferir con el renderizado del mapa
      const timeoutId = setTimeout(() => {
        logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Ejecutando cálculos para tab:', activeTab);
        
        switch (activeTab) {
          case 'builtArea':
            logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Calculando superficie construida...');
            setBuiltAreaData(calculateBuiltAreaData());
            break;
          case 'fiscal':
            logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Calculando datos fiscales...');
            setFiscalData(calculateFiscalData());
            break;
          case 'zc':
            logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Calculando ponderación ZC...');
            setZcData(calculateZCPonderacion());
            break;
        }
      }, 100); // Pequeño delay para evitar conflictos

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, activeTab, elements]);

  if (!isOpen) return null;

  return (
    <div className="v2-calculations-panel">
      <div className="v2-calculations-header">
        <h3>Cálculos</h3>
        <button className="v2-close-btn" onClick={onClose}>×</button>
      </div>

      <div className="v2-calculations-tabs">
        <button 
          className={`v2-tab ${activeTab === 'builtArea' ? 'active' : ''}`}
          onClick={() => setActiveTab('builtArea')}
        >
          Superficie Construida
        </button>
        <button 
          className={`v2-tab ${activeTab === 'fiscal' ? 'active' : ''}`}
          onClick={() => setActiveTab('fiscal')}
        >
          Cálculos Fiscales
        </button>
        <button 
          className={`v2-tab ${activeTab === 'zc' ? 'active' : ''}`}
          onClick={() => setActiveTab('zc')}
        >
          Superficie Catastral
        </button>
      </div>

      <div className="v2-calculations-content">
        {activeTab === 'builtArea' && (
          <div className="v2-tab-content">
                         <div className="v2-content-header">
               <button 
                 className="v2-export-btn"
                 onClick={() => exportToCSV(
                   builtAreaData,
                   ['Finca', 'Superficie Construida (m2)', '% del Total'],
                   'superficie_construida'
                 )}
               >
                 Exportar CSV
               </button>
             </div>
            
            {builtAreaData.length === 0 ? (
              <div className="v2-no-data">
                No hay datos de superficie construida disponibles.<br />
                Asigna valores de Finca a los elementos para ver los cálculos.
              </div>
            ) : (
              <div className="v2-table-container">
                <table className="v2-calculations-table">
                  <thead>
                    <tr>
                      <th>Finca</th>
                      <th>Superficie Construida (m²)</th>
                      <th>% del Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {builtAreaData.map(finca => (
                      <tr key={finca.fincaId}>
                        <td>{getFincaDisplayName(finca.fincaId)}</td>
                        <td>{finca.superficieConstruida.toFixed(2)}</td>
                        <td>{finca.porcentaje.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="v2-summary">
                  <strong>Resumen:</strong> {builtAreaData.length} fincas | 
                  Superficie construida total: {builtAreaData.reduce((sum, finca) => sum + finca.superficieConstruida, 0).toFixed(2)} m²
                </div>
              </div>
            )}
          </div>
        )}

                 {activeTab === 'fiscal' && (
           <div className="v2-tab-content">
             {(() => {
               logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Renderizando tab fiscal - fiscalData:', fiscalData);
               return null;
             })()}
                         <div className="v2-content-header">
               <button 
                 className="v2-export-btn"
                 onClick={() => exportToCSV(
                   fiscalData,
                   ['Finca', 'Superficie Fiscal (m2)', '% del Total'],
                   'calculos_fiscales'
                 )}
               >
                 Exportar CSV
               </button>
             </div>
            
            {fiscalData.length === 0 ? (
              <div className="v2-no-data">
                No hay datos fiscales disponibles.<br />
                Asigna valores de Finca y Cómputo a los elementos para ver los cálculos.
              </div>
            ) : (
              <div className="v2-table-container">
                <table className="v2-calculations-table">
                  <thead>
                    <tr>
                      <th>Finca</th>
                      <th>Superficie Fiscal (m²)</th>
                      <th>% del Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fiscalData.map(finca => (
                      <tr key={finca.fincaId}>
                        <td>{getFincaDisplayName(finca.fincaId)}</td>
                        <td>{finca.superficieFiscal.toFixed(2)}</td>
                        <td>{finca.porcentaje.toFixed(2)}%</td>
                      </tr>
                    ))}
                                         <tr className="v2-zonas-comunes">
                       <td>Zonas comunes</td>
                       <td>
                         {(() => {
                           const totalZonasComunes = elements
                             .filter((element) => element.category === 'ZC')
                             .reduce((sum: number, element) => sum + element.area, 0);
                           logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Renderizando zonas comunes en UI:', totalZonasComunes.toFixed(2), 'm²');
                           return totalZonasComunes.toFixed(2);
                         })()}
                       </td>
                       <td>-</td>
                     </tr>
                  </tbody>
                </table>
                                 <div className="v2-summary">
                   <strong>Resumen:</strong> {fiscalData.length} fincas | 
                   Superficie fiscal total: {fiscalData.reduce((sum, finca) => sum + finca.superficieFiscal, 0).toFixed(2)} m² | 
                   Zonas comunes total: {(() => {
                     const totalZonasComunes = elements
                       .filter((element) => element.category === 'ZC')
                       .reduce((sum: number, element) => sum + element.area, 0);
                     logger.log('CALCULATIONS_PANEL', '🔍 CalculationsPanel: Renderizando resumen zonas comunes:', totalZonasComunes.toFixed(2), 'm²');
                     return totalZonasComunes.toFixed(2);
                   })()} m²
                 </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'zc' && (
          <div className="v2-tab-content">
            <div className="v2-content-header">
              <button 
                className="v2-export-btn"
                onClick={() => exportToCSV(
                  zcData,
                  ['Finca', 'Superficie Fiscal (m2)', 'Superficie ZC Asignada (m2)', '% de la distribución', 'Superficie Total (m2)'],
                  'superficie_catastral'
                )}
              >
                Exportar CSV
              </button>
            </div>
            
            {zcData.length === 0 ? (
              <div className="v2-no-data">
                No hay datos disponibles para la superficie catastral.<br />
                Asegúrate de tener elementos de "Zonas Comunes Edificio" y fincas asignadas.
              </div>
            ) : (
              <div className="v2-table-container">
                <table className="v2-calculations-table">
                  <thead>
                    <tr>
                      <th>Finca</th>
                      <th>Superficie Fiscal (m²)</th>
                      <th>Superficie ZC Asignada (m²)</th>
                      <th>% de la distribución</th>
                      <th>Superficie Total (m²)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zcData.map(finca => (
                      <tr key={finca.fincaId}>
                        <td>{getFincaDisplayName(finca.fincaId)}</td>
                        <td>{finca.superficieFiscal.toFixed(2)}</td>
                        <td>{finca.superficieZCAsignada.toFixed(2)}</td>
                        <td>{finca.porcentajeDistribucion.toFixed(2)}%</td>
                        <td>{finca.superficieTotal.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="v2-summary">
                  <strong>Resumen:</strong> {zcData.length} fincas | 
                  Superficie fiscal total: {zcData.reduce((sum, finca) => sum + finca.superficieFiscal, 0).toFixed(2)} m² | 
                  Superficie ZC total: {zcData.reduce((sum, finca) => sum + finca.superficieZCAsignada, 0).toFixed(2)} m²
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculationsPanel;
